import React, { useEffect, useState } from "react";
import { Grid, Typography, Box, Card, CardContent } from "@mui/material";
import {
  AccountCircle,
  Email,
  Lock,
  Phone,
  AssignmentInd,
  LocationOn
} from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import "../../styles/UserStyles.css";
import "../../styles/App.scss";
import { STAFFS } from "../../component/common/Routes/AppRoutes";
import ControlledTextInputField from "../../component/ui/FormFields/ControlledTextInputField";
import CustomButton from "../../component/ui/FormFields/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  resetStaffDetail,
  staffAddRequest
} from "../../redux/Staff/StaffAction";
import { toast, ToastContainer } from "react-toastify";
import { fetchRolesRequest } from "../../redux/Roles/actions/RolesActions";
import {
  selectCityRequest,
  selectCountryRequest,
  selectStateRequest
} from "../../redux/Common/selectorAction";
import PasswordInput from "../../component/ui/PasswordInput";

export default function AddStaff() {
  const {
    handleSubmit,
    control,
    setError,
    setValue,
    formState: { errors },
    watch
  } = useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchRolesRequest());
    dispatch(selectCountryRequest());
  }, []);

  const AddStaffDetail = useSelector((state) => state.staffs?.Staff || {});
  useEffect(() => {
    dispatch(resetStaffDetail());
  }, []);
  const loading = AddStaffDetail?.loading;

  const addSuccessMessage = AddStaffDetail?.success;
  const EmailErrorMessage = AddStaffDetail?.error?.email;
  const contactErrorMessage = AddStaffDetail?.error?.contact;
  const roles = useSelector((state) => state.roles?.roles || {});
  const formValue = watch();
  const countryId = formValue.country?.value;
  const stateId = formValue.state?.value;
  const countries = useSelector((state) => state.selector?.country?.data);
  const states = useSelector((state) => state.selector?.state?.data);
  const cities = useSelector((state) => state.selector?.city?.data);
  const countryForCode = countries?.map((value) => {
    return {
      label: ` +${value.phonecode} - ${value.short_name}`,
      value: value.phonecode
    };
  });

  const role_name = roles.map((roles) => {
    return { value: roles.id, label: roles.name };
  });

  const countryName = countries?.map((country) => {
    return { value: country?.id, label: country?.country_name };
  });
  const stateName = states?.map((state) => {
    return { value: state?.id, label: state?.state_name };
  });
  const cityName = cities?.map((city) => {
    return { value: city?.id, label: city?.city_name };
  });

  useEffect(() => {
    if (countryId > 0) {
      setValue("state", "");
      setValue("city", "");
      dispatch(selectStateRequest(countryId));
    }
  }, [countryId]);

  useEffect(() => {
    if (stateId > 0) {
      setValue("city", "");
      dispatch(selectCityRequest(stateId));
    }
  }, [stateId]);

  const validateDOB = (value) => {
    const today = new Date();
    const dob = new Date(value);
    const age = today.getFullYear() - dob.getFullYear();
    const isFutureDate = dob > today;

    if (isFutureDate) {
      return "Date of Birth cannot be in the future.";
    }
    if (age < 18) {
      return "You must be at least 18 years old.";
    }
    return true;
  };
  const formFields = [
    {
      name: "first_name",
      label: "First Name",
      placeholder: "Enter First Name",
      icon: <AccountCircle />,
      rules: { required: "First Name is required" },
      sm: 6,
      xs: 12
    },
    {
      name: "last_name",
      label: "Last Name",
      placeholder: "Enter Last Name",
      icon: <AccountCircle />,
      rules: { required: "Last Name is required" },
      sm: 6,
      xs: 12
    },
    {
      name: "email",
      label: "Email Address",
      placeholder: "Enter Email Address",
      icon: <Email />,
      rules: {
        required: "Email Address is required",
        pattern: {
          value: /^\S+@\S+\.\S+$/,
          message: "Enter a valid email address"
        }
      },
      sm: 6,
      xs: 12
    },
    {
      name: "password",
      label: "Password",
      placeholder: "Enter Password",
      icon: <Lock />,
      type: "password",
      rules: {
        required: "Password is required",
        minLength: {
          value: 6,
          message: "Password must be at least 6 characters"
        }
      },
      sm: 6,
      xs: 12
    },
    {
      name: "role",
      label: "Role",
      placeholder: "Select Role",
      icon: <AssignmentInd />,

      options: role_name,
      rules: { required: "Role is required" },
      sm: 6,
      xs: 12
    },
    {
      name: "country_code",
      options: countryForCode,
      label: "code",
      placeholder: "Select Country Code",
      rules: {
        required: "Code is Required"
      },
      sm: 2,
      xs: 4
    },
    {
      name: "phone",
      label: "Mobile Number",
      placeholder: "Enter Mobile Number",
      icon: <Phone />,
      type: "Number",
      rules: {
        required: "Mobile Number is required",
        pattern: {
          value: /^\d+$/,
          message: "Enter only numeric digits"
        }
      },
      sm: 4,
      xs: 8
    },
    {
      name: "gender_type",
      label: "Gender",
      placeholder: "Select Gender",
      icon: <AssignmentInd />,
      type: "radio",
      radioOptions: [
        { value: "M", label: "Male" },
        { value: "F", label: "Female" },
        { value: "O", label: "Other" }
      ],
      rules: { required: "Gender is required" },
      sm: 6,
      xs: 12
    },
    {
      name: "dob",
      label: "Date of Birth",
      placeholder: "",
      icon: <AssignmentInd />,
      type: "date",
      rules: {
        required: "Date of Birth is required.",
        validate: validateDOB
      },
      sm: 6,
      xs: 12
    },
    {
      name: "address1",
      label: "Address1",
      placeholder: "Enter Address",
      icon: <LocationOn />,
      rules: { required: "Address is required" },
      sm: 6,
      xs: 12
    },
    {
      name: "address2",
      label: "Address2",
      placeholder: "Enter Address",
      icon: <LocationOn />,
      rules: { required: "Address is required" },
      sm: 6,
      xs: 12
    },
    {
      name: "country",
      label: "Country",
      placeholder: "Select Country",
      icon: <LocationOn />,
      rules: { required: "Country is required" },
      options: countryName,
      sm: 6,
      xs: 12
    },
    {
      name: "state",
      label: "State",
      placeholder: "Select State",
      icon: <LocationOn />,
      rules: { required: "State is required" },
      options: stateName,
      sm: 6,
      xs: 12
    },

    {
      name: "city",
      label: "City",
      placeholder: "Select City",
      icon: <LocationOn />,
      rules: { required: "City is required" },
      options: cityName,
      sm: 6,
      xs: 12
    },

    {
      name: "pincode",
      label: "Pincode",
      placeholder: "Enter Pincode",
      icon: <LocationOn />,
      type: "Number",
      rules: {
        required: "Pincode is required",
        pattern: {
          value: /^[0-9]+$/,
          message: "Enter a valid numeric pincode"
        }
      },
      sm: 6,
      xs: 12
    }
  ];
  useEffect(() => {
    if (addSuccessMessage) {
      toast.success("Staff Inserted Successfully", {
        position: "top-center",
        autoClose: 3000,
        onClose: () => {
          navigate(STAFFS);
        }
      });

      dispatch(resetStaffDetail());
    }
  }, [addSuccessMessage]);

  useEffect(() => {
    if (EmailErrorMessage) {
      setError("email", { type: "manual", message: EmailErrorMessage });
      dispatch(resetStaffDetail());
    }

    if (contactErrorMessage) {
      setError("phone", { type: "manual", message: contactErrorMessage });
      dispatch(resetStaffDetail());
    }
  }, [EmailErrorMessage, contactErrorMessage]);

  return (
    <>
      <ToastContainer />
      <Box className="container">
        <Card>
          <CardContent className="card">
            <Typography variant="h5" className="form-heading">
              Add Staff
            </Typography>
            <form
              onSubmit={handleSubmit(async (data) => {
                try {
                  await dispatch(
                    staffAddRequest({
                      ...data,
                      city: data.city?.value || data.city,
                      state: data.state?.value || data.state,
                      country: data.country?.value || data.country,
                      role_id: data.role?.value || data.role,
                      user_type: "4",
                      country_code: data.country_code?.value
                    })
                  );
                } catch (error) {
                  console.error("Error submitting staff data:", error);
                }
              })}
            >
              <Grid
                className="content-top-height"
                container
                rowSpacing={4}
                columnSpacing={{ xs: 1, sm: 1, md: 1, lg: 3 }}
              >
                {formFields.map((field) => (
                  <Grid item xs={field.xs} sm={field.sm} key={field.name}>
                    {field.name === "password" ? (
                      <PasswordInput
                        name={field.name}
                        control={control}
                        label={field.label}
                        placeholder={field.placeholder}
                        rules={field.rules}
                        error={!!errors[field.name]}
                        helperText={errors[field.name]?.message}
                      />
                    ) : (
                      <ControlledTextInputField
                        name={field.name}
                        control={control}
                        label={field.label}
                        placeholder={field.placeholder}
                        startAdornment={field.icon}
                        rules={field.rules}
                        options={field.options}
                        defaultValue={field.defaultValue}
                        type={field.type}
                        radioOptions={field.radioOptions}
                        error={!!errors[field.name]}
                        helperText={errors[field.name]?.message}
                      />
                    )}
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Box className="button-container">
                    <CustomButton
                      variant="contained"
                      className="cancel-button"
                      type="button"
                      text={"back"}
                      link={STAFFS}
                    />
                    <CustomButton
                      variant="contained"
                      className="success-button"
                      type="submit"
                      loading={loading}
                      text={"submit"}
                    />
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
